import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertCircle, DollarSign, Users, Building2, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';

export function BulkSalaryPayment({ onComplete }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [branches, setBranches] = useState([]);
  const [formData, setFormData] = useState({
    period: format(new Date(), 'MMM yyyy'),
    branch_id: '',
    funding_branch_id: '',
    payment_mode: 'bank',
    notes: 'Monthly salary payment',
    date: format(new Date(), 'yyyy-MM-dd'),
    selectedEmployees: []
  });
  const [selectAll, setSelectAll] = useState(true);

  useEffect(() => {
    if (open) {
      fetchBranches();
    }
  }, [open]);

  const fetchBranches = async () => {
    try {
      const res = await api.get('/branches');
      setBranches(res.data);
    } catch {}
  };

  const fetchPreview = async () => {
    setPreviewing(true);
    try {
      const params = new URLSearchParams();
      params.set('period', formData.period);
      if (formData.branch_id) params.set('branch_id', formData.branch_id);
      const res = await api.get(`/salary-payments/bulk-preview?${params.toString()}`);
      setPreview(res.data);
      setSelectAll(true);
      setFormData(f => ({ ...f, selectedEmployees: res.data.to_pay.map(e => e.id) }));
    } catch (err) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewing(false);
    }
  };

  const getMonthOptions = () => {
    const months = [];
    for (let i = -2; i <= 12; i++) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      months.push(d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
    }
    return months;
  };

  const handleSubmit = async () => {
    if (!formData.period) {
      toast.error('Please select a period');
      return;
    }
    if (formData.selectedEmployees.length === 0) {
      toast.error('No employees selected');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/salary-payments/bulk', {
        period: formData.period,
        branch_id: formData.branch_id || null,
        funding_branch_id: formData.funding_branch_id || null,
        payment_mode: formData.payment_mode,
        // iter862 — per-row payment_mode overrides. Only send entries
        // that differ from the top-level default so payload stays lean.
        employee_payment_modes: Object.fromEntries(
          Object.entries(formData.perEmpModes || {}).filter(([, v]) => v && v !== formData.payment_mode)
        ),
        notes: formData.notes,
        date: new Date(formData.date).toISOString(),
        employee_ids: selectAll ? null : formData.selectedEmployees
      });
      setResult(res.data);
      // iter864 — 20-second countdown Undo toast. If clicked, reverses
      // the entire batch (all salary_payments, expenses, notifications,
      // and loan installments) via /salary-payments/bulk/{batch}/undo.
      // The backend enforces a 5-minute hard cap.
      const batchId = res.data.bulk_batch_id;
      if (batchId) {
        const t = toast.success(
          `Paid ${res.data.summary.total_paid} employees — ${cur()} ${res.data.summary.total_amount.toFixed(2)}`,
          {
            duration: 20000,
            action: {
              label: 'Undo',
              onClick: async () => {
                try {
                  const r = await api.post(`/salary-payments/bulk/${batchId}/undo`);
                  const rv = r.data.reversed || {};
                  toast.warning(
                    `Reversed: ${rv.salary_payments || 0} pays, ${rv.loan_installments || 0} loan installments restored`,
                    { duration: 6000 }
                  );
                  setResult(null);
                  fetchPreview();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Undo failed — grace window may have expired');
                }
              },
            },
          }
        );
        // Suppress the plain success toast when we already showed the undo one.
        void t;
      } else {
        toast.success(`Paid ${res.data.summary.total_paid} employees - ${cur()} ${res.data.summary.total_amount.toFixed(2)}`);
      }
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process payments');
    } finally {
      setLoading(false);
    }
  };

  const toggleEmployee = (empId) => {
    setFormData(f => ({
      ...f,
      selectedEmployees: f.selectedEmployees.includes(empId)
        ? f.selectedEmployees.filter(id => id !== empId)
        : [...f.selectedEmployees, empId]
    }));
    setSelectAll(false);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectAll(false);
      setFormData(f => ({ ...f, selectedEmployees: [] }));
    } else {
      setSelectAll(true);
      setFormData(f => ({ ...f, selectedEmployees: preview?.to_pay?.map(e => e.id) || [] }));
    }
  };

  const resetDialog = () => {
    setPreview(null);
    setResult(null);
    setFormData({
      period: format(new Date(), 'MMM yyyy'),
      branch_id: '',
      payment_mode: 'bank',
      notes: 'Monthly salary payment',
      date: format(new Date(), 'yyyy-MM-dd'),
      selectedEmployees: [],
      perEmpModes: {},
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetDialog(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-xl" data-testid="bulk-salary-btn">
          <Users size={14} className="mr-1" /> {t('bulk_salary_title')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto" data-testid="bulk-salary-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <DollarSign size={20} className="text-primary" />
            {t('bulk_salary_title')}
          </DialogTitle>
        </DialogHeader>

        {/* Result View */}
        {result && (
          <div className="space-y-4">
            <div className="p-4 bg-success/10 rounded-xl border border-success/30">
              <div className="flex items-center gap-2 text-success mb-2">
                <CheckCircle size={20} />
                <span className="font-bold">{t('payment_completed')}!</span>
                {result.bulk_batch_id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="ml-auto h-7 text-[11px] border-warning/50 text-warning hover:bg-warning/10"
                    onClick={async () => {
                      try {
                        const r = await api.post(`/salary-payments/bulk/${result.bulk_batch_id}/undo`);
                        const rv = r.data.reversed || {};
                        toast.warning(
                          `Reversed: ${rv.salary_payments || 0} pays, ${rv.loan_installments || 0} loan installments restored`,
                          { duration: 6000 }
                        );
                        setResult(null);
                        fetchPreview();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Undo failed — grace window may have expired');
                      }
                    }}
                    data-testid="bulk-undo-inline-btn"
                    title="Reverses every DB write for this payroll run. Only works within 5 minutes of posting."
                  >
                    Undo this batch
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-success">{result.summary.total_paid}</p>
                  <p className="text-xs text-muted-foreground">{t('employees_to_pay')}</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-primary">{cur()} {result.summary.total_amount.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">{t('total_amount')}</p>
                </div>
              </div>
            </div>

            {/* Branch Breakdown */}
            {Object.keys(result.branch_totals || {}).length > 0 && (
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{t('by_branch')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(result.branch_totals).map(([branch, data]) => (
                      <div key={branch} className="flex justify-between items-center p-2 bg-secondary/50 rounded-lg">
                        <span className="text-sm font-medium">{branch}</span>
                        <div className="text-right">
                          <span className="text-sm">{data.count} employees</span>
                          <span className="text-sm font-bold ml-2">{cur()} {data.amount.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Skipped/Failed */}
            {(result.details.skipped?.length > 0 || result.details.failed?.length > 0) && (
              <Card className="border-border border-warning/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-1">
                    <AlertCircle size={14} className="text-warning" />
                    {t('skipped_failed')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {result.details.skipped?.map((emp, i) => (
                      <div key={i} className="flex justify-between items-center text-xs p-1.5 bg-warning/10 rounded">
                        <span>{emp.name}</span>
                        <Badge variant="outline" className="text-[10px]">{emp.reason}</Badge>
                      </div>
                    ))}
                    {result.details.failed?.map((emp, i) => (
                      <div key={i} className="flex justify-between items-center text-xs p-1.5 bg-error/10 rounded">
                        <span>{emp.name}</span>
                        <Badge className="bg-error/20 text-error text-[10px]">{t('failed')}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Button className="w-full rounded-xl" onClick={() => { setOpen(false); resetDialog(); }}>
              {t('done')}
            </Button>
          </div>
        )}

        {/* Preview View */}
        {!result && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div className="p-3 bg-primary/10 rounded-xl text-center">
                <p className="text-2xl font-bold text-primary">{preview.to_pay_count}</p>
                <p className="text-xs text-muted-foreground">{t('to_pay')}</p>
              </div>
              <div className="p-3 bg-success/10 rounded-xl text-center">
                <p className="text-2xl font-bold text-success">{preview.already_paid_count}</p>
                <p className="text-xs text-muted-foreground">{t('already_paid')}</p>
              </div>
              <div className="p-3 bg-muted rounded-xl text-center">
                <p className="text-2xl font-bold">{cur()} {preview.to_pay_total.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">{t('total_amount')}</p>
              </div>
            </div>

            {/* iter862 — Loan auto-deduction projection banner */}
            {preview.auto_deduction_total > 0 && (
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="bulk-autodeduct-banner">
                <div className="flex-1">
                  <p className="text-xs font-medium text-amber-900">Loan auto-deductions projected for this run</p>
                  <p className="text-[11px] text-amber-700 mt-0.5">
                    Gross {cur()} {preview.to_pay_total.toFixed(2)} · auto-deduct {cur()} {preview.auto_deduction_total.toFixed(2)} · net cash out {cur()} {(preview.net_disbursement_total ?? (preview.to_pay_total - preview.auto_deduction_total)).toFixed(2)}
                  </p>
                </div>
                <span className="text-lg font-bold text-amber-700 tabular-nums">−{cur()} {preview.auto_deduction_total.toFixed(2)}</span>
              </div>
            )}

            {/* Employee List */}
            {preview.to_pay?.length > 0 && (
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center gap-2 flex-wrap">
                    <CardTitle className="text-sm">{t('employees_to_pay')} ({preview.to_pay_count})</CardTitle>
                    <div className="flex items-center gap-3">
                      {/* iter863 — Auto-suggest button. Copies each row's
                          suggested_payment_mode (employee.preferred → branch.default → 'bank')
                          into perEmpModes with one click. */}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[11px]"
                        onClick={() => {
                          const suggestions = Object.fromEntries(
                            (preview.to_pay || [])
                              .filter(e => e.suggested_payment_mode)
                              .map(e => [e.id, e.suggested_payment_mode])
                          );
                          setFormData(f => ({ ...f, perEmpModes: { ...(f.perEmpModes || {}), ...suggestions } }));
                          const bySrc = (preview.to_pay || []).reduce((m, e) => {
                            const s = e.suggested_payment_mode_source || 'default';
                            m[s] = (m[s] || 0) + 1; return m;
                          }, {});
                          toast.success(`Applied — ${bySrc.employee || 0} from employee prefs, ${bySrc.branch || 0} from branch rules, ${bySrc.default || 0} fell back to bank`);
                        }}
                        data-testid="bulk-autosuggest-btn"
                        title="Apply per-employee preferred mode → branch default → bank"
                      >
                        Auto-suggest cash/bank
                      </Button>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectAll}
                          onCheckedChange={toggleSelectAll}
                          id="select-all"
                        />
                        <Label htmlFor="select-all" className="text-xs cursor-pointer">{t('select_all')}</Label>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {preview.to_pay.map((emp) => {
                      const dedn = Number(emp.auto_deduction_projected || 0);
                      const net = Number(emp.net_after_auto_deduction ?? emp.salary);
                      const rowMode = formData.perEmpModes?.[emp.id] || formData.payment_mode;
                      return (
                        <div key={emp.id} className="flex items-center gap-2 p-2 hover:bg-secondary/50 rounded-lg" data-testid={`bulk-row-${emp.id}`}>
                          <Checkbox
                            checked={selectAll || formData.selectedEmployees.includes(emp.id)}
                            onCheckedChange={() => toggleEmployee(emp.id)}
                            id={`emp-${emp.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{emp.name}</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-xs text-muted-foreground">{emp.branch_name}</p>
                              {dedn > 0 && (
                                <span
                                  className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 text-[10px] font-medium"
                                  title={`Loan auto-deduction: SAR ${dedn.toFixed(2)} — net paid to bank/cash: SAR ${net.toFixed(2)}`}
                                  data-testid={`bulk-autodeduct-${emp.id}`}
                                >
                                  −{cur()} {dedn.toFixed(2)} loan
                                </span>
                              )}
                            </div>
                          </div>
                          {/* iter862 — per-row payment mode override */}
                          <Select
                            value={rowMode}
                            onValueChange={(v) => setFormData(f => ({ ...f, perEmpModes: { ...(f.perEmpModes || {}), [emp.id]: v } }))}
                          >
                            <SelectTrigger className="h-7 w-24 text-[11px]" data-testid={`bulk-mode-${emp.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="bank">Bank</SelectItem>
                              <SelectItem value="cash">Cash</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="text-right">
                            <span className="text-sm font-bold block">{cur()} {emp.salary.toFixed(2)}</span>
                            {dedn > 0 && (
                              <span className="text-[10px] text-muted-foreground block">net {cur()} {net.toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {preview.to_pay?.length === 0 && (
              <div className="p-6 text-center bg-success/10 rounded-xl">
                <CheckCircle size={32} className="mx-auto text-success mb-2" />
                <p className="text-sm font-medium">{t('already_paid')} {formData.period}!</p>
              </div>
            )}

            {/* Already Paid */}
            {preview.already_paid?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  {preview.already_paid_count} {t('already_paid')}
                </summary>
                <div className="mt-2 p-2 bg-success/5 rounded-lg space-y-1">
                  {preview.already_paid.map((emp, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{emp.name}</span>
                      <span className="text-success">{cur()} {emp.salary.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Payment Options */}
            <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-xl">
              <div>
                <Label className="text-xs">{t('payment_mode')}</Label>
                <Select value={formData.payment_mode} onValueChange={(v) => setFormData(f => ({ ...f, payment_mode: v }))}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">{t('bank')}</SelectItem>
                    <SelectItem value="cash">{t('cash')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('payment_date')}</Label>
                <Input 
                  type="date" 
                  value={formData.date} 
                  onChange={(e) => setFormData(f => ({ ...f, date: e.target.value }))}
                  className="h-9"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setPreview(null)} className="rounded-xl">
                {t('back')}
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={loading || (preview.to_pay_count === 0)}
                className="rounded-xl"
                data-testid="confirm-bulk-pay"
              >
                {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <DollarSign size={14} className="mr-1" />}
                {t('pay_employees')} ({selectAll ? preview.to_pay_count : formData.selectedEmployees.length})
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Initial Form */}
        {!result && !preview && (
          <div className="space-y-4">
            <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
              <div className="flex items-center gap-2 text-primary mb-2">
                <AlertCircle size={16} />
                <span className="text-sm font-medium">{t('bulk_salary_title')}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('bulk_payment_info')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">{t('period')} *</Label>
                <Select value={formData.period} onValueChange={(v) => setFormData(f => ({ ...f, period: v }))}>
                  <SelectTrigger data-testid="bulk-period-select">
                    <SelectValue placeholder={t('period')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getMonthOptions().map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('branch')}</Label>
                <Select value={formData.branch_id || "all"} onValueChange={(v) => setFormData(f => ({ ...f, branch_id: v === "all" ? "" : v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('all_branches')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('all_branches')}</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* iter962 — Funding Branch: which branch physically pays this run.
                When set to a branch other than an employee's own, the salary
                expense nets into the Inter-Branch ledger. */}
            <div>
              <Label className="text-xs">Funding Branch (who pays)</Label>
              <Select value={formData.funding_branch_id || "own"} onValueChange={(v) => setFormData(f => ({ ...f, funding_branch_id: v === "own" ? "" : v }))}>
                <SelectTrigger data-testid="bulk-funding-branch">
                  <SelectValue placeholder="Each employee's own branch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="own">Each employee's own branch (no netting)</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name} funds all</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.funding_branch_id && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Salaries for other branches will post as inter-branch dues owed to this funding branch.
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs">{t('notes')}</Label>
              <Input 
                value={formData.notes} 
                onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))}
                placeholder={t('notes')}
              />
            </div>

            <Button 
              onClick={fetchPreview} 
              disabled={previewing || !formData.period}
              className="w-full rounded-xl"
              data-testid="preview-bulk-pay"
            >
              {previewing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Users size={14} className="mr-1" />}
              {t('preview_select')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
